import {
  Box,
  VStack,
  Text,
  Card,
  CardBody,
  HStack,

  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Badge,
  Grid,

  IconButton,
  Tooltip,
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import {
  MdZoomIn,
  MdZoomOut,
  MdRotateRight,
  MdBrightness6,
  MdContrast,
  MdRefresh,
  MdViewInAr
} from 'react-icons/md'

interface DicomViewerProps {
  dicomImages: string[]
  analyzing: boolean
  hasFiles: boolean
}

export default function DicomViewer({
  dicomImages,
  analyzing,
  hasFiles,
}: DicomViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [currentSlice, setCurrentSlice] = useState(0)
  const [zoom, setZoom] = useState(1)
  const [brightness, setBrightness] = useState(100)
  const [contrast, setContrast] = useState(100)
  const [rotation, setRotation] = useState(0)
  const [, ] = useState<'2D' | '3D'>('2D')

  // Simulate DICOM rendering (placeholder - in real implementation would use cornerstone.js)
  useEffect(() => {
    if (!hasFiles || !canvasRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Mock DICOM slice rendering
    ctx.fillStyle = '#000000'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Draw mock medical image
    ctx.fillStyle = '#1a1a1a'
    ctx.fillRect(50, 50, canvas.width - 100, canvas.height - 100)

    // Apply transformations (mock)
    ctx.save()
    ctx.translate(canvas.width / 2, canvas.height / 2)
    ctx.rotate((rotation * Math.PI) / 180)
    ctx.scale(zoom, zoom)
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`

    // Draw mock anatomical structures
    ctx.fillStyle = '#404040'
    ctx.beginPath()
    ctx.ellipse(0, 0, 100, 80, 0, 0, 2 * Math.PI)
    ctx.fill()

    ctx.fillStyle = '#606060'
    ctx.beginPath()
    ctx.ellipse(-30, -20, 20, 15, 0, 0, 2 * Math.PI)
    ctx.fill()

    ctx.fillStyle = '#505050'
    ctx.beginPath()
    ctx.ellipse(30, -20, 20, 15, 0, 0, 2 * Math.PI)
    ctx.fill()

    ctx.restore()

    // Draw slice info
    ctx.fillStyle = '#00ff00'
    ctx.font = '12px monospace'
    ctx.fillText(`Slice: ${currentSlice + 1}/${dicomImages.length || 1}`, 10, 20)
    ctx.fillText(`Zoom: ${(zoom * 100).toFixed(0)}%`, 10, 40)
    ctx.fillText(`Brightness: ${brightness}`, 10, 60)
    ctx.fillText(`Contrast: ${contrast}`, 10, 80)

  }, [dicomImages, currentSlice, zoom, brightness, contrast, rotation, hasFiles])

  const handleReset = () => {
    setZoom(1)
    setBrightness(100)
    setContrast(100)
    setRotation(0)
    setCurrentSlice(0)
  }

  if (!hasFiles) {
    return (
      <VStack spacing={4} py={8} color="gray.500">
        <MdViewInAr style={{ fontSize: '64px' }} />
        <Text textAlign="center">
          Upload DICOM files to view medical images
        </Text>
        <Text textAlign="center" fontSize="sm" color="gray.400">
          The viewer supports 2D slice navigation and basic image manipulation
        </Text>
      </VStack>
    )
  }

  return (
    <VStack spacing={4} align="stretch" h="100%">
      {/* Viewer Canvas */}
      <Card flex={1} bg="black">
        <CardBody p={0} position="relative">
          <Box
            position="relative"
            w="100%"
            h="500px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="black"
          >
            <canvas
              ref={canvasRef}
              width={512}
              height={512}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
            {analyzing && (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg="blackAlpha.700"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <VStack spacing={2}>
                  <Text color="white" fontWeight="bold">
                    Analyzing...
                  </Text>
                </VStack>
              </Box>
            )}
          </Box>
        </CardBody>
      </Card>

      {/* Controls */}
      <Card>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {/* Slice Navigation */}
            {dicomImages.length > 1 && (
              <VStack spacing={2} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="semibold">
                    Slice Navigation
                  </Text>
                  <Badge colorScheme="blue">
                    {currentSlice + 1} / {dicomImages.length}
                  </Badge>
                </HStack>
                <Slider
                  value={currentSlice}
                  min={0}
                  max={Math.max(0, dicomImages.length - 1)}
                  onChange={setCurrentSlice}
                  colorScheme="blue"
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
              </VStack>
            )}

            {/* Tools */}
            <Grid templateColumns="repeat(4, 1fr)" gap={2}>
              <Tooltip label="Zoom In">
                <IconButton
                  aria-label="Zoom in"
                  icon={<MdZoomIn />}
                  onClick={() => setZoom(Math.min(3, zoom + 0.25))}
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                />
              </Tooltip>
              <Tooltip label="Zoom Out">
                <IconButton
                  aria-label="Zoom out"
                  icon={<MdZoomOut />}
                  onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                />
              </Tooltip>
              <Tooltip label="Rotate">
                <IconButton
                  aria-label="Rotate"
                  icon={<MdRotateRight />}
                  onClick={() => setRotation((rotation + 90) % 360)}
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                />
              </Tooltip>
              <Tooltip label="Reset View">
                <IconButton
                  aria-label="Reset"
                  icon={<MdRefresh />}
                  onClick={handleReset}
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                />
              </Tooltip>
            </Grid>

            {/* Brightness */}
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <HStack>
                  <MdBrightness6 />
                  <Text fontSize="sm">Brightness</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  {brightness}%
                </Text>
              </HStack>
              <Slider
                value={brightness}
                min={50}
                max={150}
                onChange={setBrightness}
                colorScheme="blue"
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </VStack>

            {/* Contrast */}
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <HStack>
                  <MdContrast />
                  <Text fontSize="sm">Contrast</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  {contrast}%
                </Text>
              </HStack>
              <Slider
                value={contrast}
                min={50}
                max={150}
                onChange={setContrast}
                colorScheme="blue"
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </VStack>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  )
}
